import BoldText from '@/components/BoldText';
import CustomBadge from '@/components/CustomBadge';
import { DateOrder, InputMethod, PageType, StatusType, TriggerType } from '@/constants/enum';
import { choiceMethodList, optionAgeList, optionDateOrderList, status } from '@/constants/options';
import useLimitPlan from '@/hooks/useLimitPlan';
import userPlans from '@/hooks/userPlans';
import SelectCustomPage from '@/pages/StoreVerificationSetup/components/CustomPage';
import ProductOrCollectionList from '@/pages/StoreVerificationSetup/components/ProductOrCollectionList';
import { handleChangePreview, previewSelector } from '@/redux/slice/preview.slice';
import {
  BlockStack,
  Card,
  ChoiceList,
  ChoiceListProps,
  Collapsible,
  Divider,
  RadioButton,
  Select,
  TextField,
} from '@shopify/polaris';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CustomTags from './CustomTags';

function Info({ error, setError }: { error: string; setError: React.Dispatch<React.SetStateAction<string>> }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['popup', 'common']);
  const { isUserFreeLimited } = userPlans();
  const { isShowUpgradeNowRulePopup } = useLimitPlan();

  const previewDetail = useSelector(previewSelector);

  const [errorSpecific, setErrorSpecific] = useState('');
  const [listUrl, setListUrl] = useState<{ value: string; isEdit: boolean; error?: string; index: number }[]>([]);

  const dateOrderHelpText = {
    [DateOrder.YY_MM_DD]: t('preview_yy_mm_dd'),
    [DateOrder.MM_DD_YY]: t('preview_mm_dd_yy'),
    [DateOrder.DD_MM_YY]: t('preview_dd_mm_yy'),
    [DateOrder.YY_DD_MM]: t('preview_yy_dd_mm'),
    [DateOrder.MM_YY_DD]: t('preview_mm_yy_dd'),
    [DateOrder.DD_YY_MM]: t('preview_dd_yy_mm'),
  };

  const triggerCondition: ChoiceListProps['choices'] = [
    { label: t ? t('always_show', { ns: 'popup' }) : 'Always show', value: TriggerType.AlwaysShow },
    {
      label: t ? t('logged_customers', { ns: 'popup' }) : 'Logged customers',
      value: TriggerType.LoggedCustomers,
      disabled: isUserFreeLimited,
    },
  ];

  const handleChangeTemplate = (key: keyof typeof previewDetail) => (value: string) => {
    const newValue = key === 'status' ? Boolean(value === StatusType.Enable) : value;
    dispatch(
      handleChangePreview({
        [key]: newValue,
      }),
    );
  };

  const handleChangeChoiceList = (key: keyof typeof previewDetail) => (selected: string[]) => {
    if (errorSpecific) {
      setErrorSpecific('');
    }

    dispatch(
      handleChangePreview({
        [key]: selected[0],
        showInPages: key === 'pageTypeToShow' ? [] : previewDetail.showInPages,
      }),
    );
  };

  return (
    <BlockStack gap="200">
      <Card>
        <BoldText>
          {t('pop_up_info')} {isShowUpgradeNowRulePopup && <CustomBadge />}
        </BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="200">
          <Select
            value={!!previewDetail.status ? StatusType.Enable : StatusType.Disable}
            label={<BoldText>{t('common:status')}</BoldText>}
            options={status(t)}
            onChange={handleChangeTemplate('status')}
            disabled={isShowUpgradeNowRulePopup}
          />

          <TextField
            autoComplete=""
            label={<b>{t('common:name')}</b>}
            value={previewDetail?.name || ''}
            onChange={(value) => {
              if (!value?.trim()?.length) {
                setError(t('rule_name_required'));
              } else {
                if (error.length) {
                  setError('');
                }
              }
              handleChangeTemplate('name')(value);
            }}
            maxLength={255}
            showCharacterCount
            placeholder={t('enter_your_pop_up_name')}
            helpText={t('for_internal_reference_only_you_can_see_it')}
            error={error}
            requiredIndicator
            disabled={isShowUpgradeNowRulePopup}
          />

          <ChoiceList
            choices={choiceMethodList(t)}
            selected={[String(previewDetail.inputMethod)]}
            title={<BoldText tone={isShowUpgradeNowRulePopup ? 'disabled' : 'base'}>{t('method')}</BoldText>}
            onChange={handleChangeChoiceList('inputMethod')}
            disabled={isShowUpgradeNowRulePopup}
          />

          <Collapsible
            open={previewDetail.inputMethod === InputMethod.BIRTHDATE_ENTRY}
            id="select-specific-page"
            expandOnPrint
            transition={{ duration: '300ms' }}
          >
            <BlockStack gap="200">
              <Select
                value={previewDetail.minAge}
                label={<BoldText>{t('verify_age')}</BoldText>}
                options={optionAgeList}
                onChange={handleChangeTemplate('minAge')}
                disabled={isShowUpgradeNowRulePopup}
              />

              <Select
                options={optionDateOrderList}
                label={<BoldText>{t('date_order')}</BoldText>}
                value={previewDetail.dateTimeFormat}
                onChange={handleChangeTemplate('dateTimeFormat')}
                helpText={dateOrderHelpText[previewDetail.dateTimeFormat]}
                disabled={isShowUpgradeNowRulePopup}
              />
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card>

      <Card>
        <BoldText>
          {t('condition')} {(isUserFreeLimited || isShowUpgradeNowRulePopup) && <CustomBadge />}
        </BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="200">
          <BlockStack gap="050">
            <BoldText>{t('display_pages')}</BoldText>
            <BlockStack>
              <RadioButton
                label={t('all_pages')}
                checked={previewDetail.pageTypeToShow === PageType.All}
                id={PageType.All}
                onChange={() => handleChangeChoiceList('pageTypeToShow')([PageType.All])}
                disabled={isShowUpgradeNowRulePopup}
              />
              <RadioButton
                label={t('home_page')}
                checked={previewDetail.pageTypeToShow === PageType.Home}
                id={PageType.Home}
                onChange={() => handleChangeChoiceList('pageTypeToShow')([PageType.Home])}
                disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
              />
              <RadioButton
                label={t('specific_collections')}
                checked={previewDetail.pageTypeToShow === PageType.SpecificCollections}
                id={PageType.SpecificCollections}
                onChange={() => handleChangeChoiceList('pageTypeToShow')([PageType.SpecificCollections])}
                disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
              />
              {previewDetail.pageTypeToShow === PageType.SpecificCollections ? (
                <ProductOrCollectionList
                  errorProductOrCollection={errorSpecific}
                  setErrorProductOrCollection={setErrorSpecific}
                />
              ) : null}
              <RadioButton
                label={t('specific_products')}
                checked={previewDetail.pageTypeToShow === PageType.SpecificProducts}
                id={PageType.SpecificProducts}
                onChange={() => handleChangeChoiceList('pageTypeToShow')([PageType.SpecificProducts])}
                disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
              />
              {previewDetail.pageTypeToShow === PageType.SpecificProducts ? (
                <ProductOrCollectionList
                  errorProductOrCollection={errorSpecific}
                  setErrorProductOrCollection={setErrorSpecific}
                />
              ) : null}

              <RadioButton
                label={t('specific_product_tags')}
                checked={previewDetail.pageTypeToShow === PageType.SpecificTags}
                id={PageType.SpecificTags}
                onChange={() => handleChangeChoiceList('pageTypeToShow')([PageType.SpecificTags])}
                disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
              />
              <CustomTags errorSpecific={errorSpecific} setErrorSpecific={setErrorSpecific} />

              <RadioButton
                label={t('custom')}
                checked={previewDetail.pageTypeToShow === PageType.Custom}
                id={PageType.Custom}
                onChange={() => handleChangeChoiceList('pageTypeToShow')([PageType.Custom])}
                disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
              />
              {previewDetail.pageTypeToShow === PageType.Custom ? (
                <SelectCustomPage listUrl={listUrl} setListUrl={setListUrl} />
              ) : null}
            </BlockStack>
          </BlockStack>

          <ChoiceList
            choices={triggerCondition}
            selected={[String(previewDetail.applyCustomer)]}
            title={<BoldText>{t('trigger_condition')}</BoldText>}
            onChange={handleChangeChoiceList('applyCustomer')}
            disabled={isShowUpgradeNowRulePopup}
          />
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

export default Info;
